import type {RepositoryNWO} from '@github-ui/current-repository'
import type {CombinedStatusResult} from '@github-ui/commit-checks-status'
import {commitStatusDetailsPath} from '@github-ui/paths'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useState} from 'react'

/**
 * Hook for fetching commit checks status with support for live updates.
 * Automatically fetches data when oid or repo changes, and provides a refresh function
 * that bypasses any caching mechanisms for real-time updates.
 */
export function useLiveCommitChecksStatus(
  oid: string,
  repo: RepositoryNWO,
): [CombinedStatusResult | undefined, () => Promise<void>] {
  const [data, setData] = useState<CombinedStatusResult | undefined>()

  const refreshChecksStatus = useCallback(async (): Promise<void> => {
    if (!oid || !repo) {
      setData(undefined)
      return
    }

    const url = commitStatusDetailsPath(repo, oid)
    const response = await verifiedFetchJSON(url)

    if (response.ok) {
      const checksData: CombinedStatusResult = await response.json()
      setData(checksData)
    }
  }, [oid, repo])

  return [data, refreshChecksStatus]
}
